import BookIcon from '@mui/icons-material/Book';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import { Avatar, Box, Button, Card, Typography } from '@mui/material';
import moment from 'moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { OfferListItem, OfferStatus } from '../types';

interface OfferCardProps {
	offer: OfferListItem;
	onSendOffer?: (offerId: string) => void;
	onEditOffer?: (offerId: string) => void;
	onChat?: (request_id: string) => void;
	onViewDetails?: (offerId: string) => void;
	status?: OfferStatus;
}

export const OfferCard: React.FC<OfferCardProps> = ({
	offer,
	onSendOffer,
	onEditOffer,
	onChat,
	onViewDetails,
	status = 'active',
}) => {
	const { t } = useTranslation();

	const {
		id,
		crops,
		farmer,
		service,
		services,
		is_sent,
		is_paid,
		request_id,
		request_active_for_days,
		channelId,
		request_active_days,
		payment_status,
		end_date,
	} = offer;
	
	// Log payment status for debugging
	console.log(`Offer ${id} payment_status: ${payment_status}, is_paid: ${is_paid}`);

	// Get crop info (API returns array, we use the first one)
	const crop = crops?.[0] || { name: '', image_url: '' };

	// Get service info (could be in service or services property)
	const serviceName = service?.name || services?.name || '';
	const servicePrice = service?.price || services?.price || 0;
	const serviceCurrency = offer.currency || services?.unit || 'EUR';
	const serviceDuration = services?.duration || '5';

	// Calculate time remaining based on end_date
	const timeRemaining = useMemo(() => {
		if (end_date) {
			try {
				// For ISO format like "2025-07-25T13:15:08.000Z"
				const endDateObj = new Date(end_date);
				
				if (endDateObj instanceof Date && !isNaN(endDateObj.getTime())) {
					const currentDate = new Date();
					console.log('End date from API:', endDateObj);
					console.log('Current date:', currentDate);
					
					// Calculate time difference
					const diffTime = endDateObj.getTime() - currentDate.getTime();
					console.log('Diff time (ms):', diffTime);
					
					// Convert to hours and days
					const diffHours = Math.ceil(diffTime / (1000 * 60 * 60));
					const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
					console.log('Hours remaining:', diffHours);
					console.log('Days remaining:', diffDays);
					
					// If time is in the future
					if (diffTime > 0) {
						// If less than 1 day remains, show hours
						if (diffTime < 24 * 60 * 60 * 1000) {
							console.log('Using hours:', diffHours);
							return { value: diffHours, unit: 'hours' };
						} else {
							console.log('Using days:', diffDays);
							return { value: diffDays, unit: 'days' };
						}
					} else {
						// If time has expired, show 0 days
						console.log('Time expired, showing 0 days');
						return { value: 0, unit: 'days' };
					}
				}
			} catch (error) {
				console.error('Error calculating time remaining:', error);
			}
		}
		
		// Fallback to active_for_days or default values
		const days = Number(request_active_days || request_active_for_days || 3);
		console.log('Using fallback days:', days);
		return { value: days, unit: 'days' };
	}, [end_date, request_active_days, request_active_for_days]);

	// Calculate status message based on payment status
	let statusMessage = '';
	if (is_paid) {
		// Paid status - "Chat closes in X days/hours"
		if (timeRemaining.unit === 'hours') {
			statusMessage = `Chat closes in ${timeRemaining.value} hours`;
		} else {
			statusMessage = `Chat closes in ${timeRemaining.value} days`;
		}
	} else {
		// Unpaid status - "Request active for X days"
		if (timeRemaining.value > 0) {
			statusMessage = `Request active for ${timeRemaining.value} days`;
		} else {
			// If days are negative or zero, show a generic message
			statusMessage = t('requestActive');
		}
	}

	const handleClick = () => {
		if (onViewDetails) {
			onViewDetails(id);
		}
	};

	return (
		<Card
			onClick={handleClick}
			sx={{
				borderRadius: '12px',
				cursor: 'pointer',
				boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.3)',
				height: '100%',
				display: 'flex',
				flexDirection: 'column',
			}}
		>
			{/* Top Section with Crop Info */}
			<Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
				<Box sx={{ display: 'flex', gap: 2 }}>
					<Avatar
						src={crop.image_url}
						alt={crop.name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<Box>
						<Typography variant="h5" sx={{ fontWeight: 500, mb: 1 }}>
							{crop.name}
						</Typography>
						<Typography
							variant="subtitle1"
							sx={{
								color: '#3da08f',
								fontWeight: 500,
								mb: 1,
								borderBottom: '2px solid #3da08f',
								display: 'inline-block',
								paddingBottom: '2px',
							}}
						>
							{serviceName}
						</Typography>
						<Typography color="text.secondary" sx={{ mb: 0.5, fontWeight: '400 !important' }}>
							{t('serviceDeliveryDays', { days: serviceDuration })}
						</Typography>
					</Box>
				</Box>
			</Box>

			{/* Middle Section with Farmer Info */}
			<Box sx={{ p: 3, borderBottom: '1px solid #f0f0f0' }}>
				<Box sx={{ display: 'flex', gap: 2 }}>
					<Avatar
						src={farmer.profile_image_url}
						alt={farmer.full_name}
						sx={{ width: 60, height: 60, borderRadius: '12px' }}
					/>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: 500, mb: 1 }}>
							{farmer.full_name}
						</Typography>

						{/* Fieldbook Button */}
						<Button
							startIcon={<BookIcon />}
							onClick={(e) => e.stopPropagation()}
							disabled
							sx={{
								textTransform: 'none',
								padding: '0',
								color: '#3da08f',
								fontWeight: 500,
								'&:hover': {
									backgroundColor: 'transparent',
									textDecoration: 'underline',
								},
							}}
						>
							{t('fieldbook')}
						</Button>
					</Box>

					{/* Price Display */}
					{servicePrice && (
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								ml: 'auto',
								width: '64px',
								height: '64px',
								backgroundColor: is_paid ? '#EBFDF9' : is_sent ? '#FFF7EA' : '#F8F9FA',
								borderRadius: '8px',
								border: is_paid
									? '1px solid #00AB89'
									: is_sent
										? '1px solid #FFAA2B'
										: '1px solid #E9ECEF',
							}}
						>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 'bold',
									color: '#343a40',
								}}
							>
								{serviceCurrency === 'EUR' ? '€' : serviceCurrency}
								{servicePrice}
							</Typography>
						</Box>
					)}
				</Box>
			</Box>

			{/* Bottom Section with Status and Actions */}
			<Box
				sx={{
					p: 2,
					mt: 'auto',
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					backgroundColor: '#F8F9FA',
				}}
			>
				{status === 'active' && (
				<Typography variant="body2" color="text.secondary" sx={{ fontWeight: '400 !important' }}>
					{statusMessage}
				</Typography>
				)}

				<Typography variant="h6" sx={{ ml: status === 'active' ? 'auto' : '0', mr: 2, fontWeight: '400 !important' }}>
					{service?.delivery_date
						? moment(service.delivery_date).format('DD.MM.YYYY')
						: offer.end_date
							? moment(offer.end_date).format('DD.MM.YYYY')
							: ''}
				</Typography>
			</Box>

			{/* Action Buttons - Only show for active offers */}
			{status === 'active' && (
			<Box
				sx={{ p: 3, pt: 0, display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}
			>
				<Button
					variant={is_paid ? 'contained' : 'text'}
					disabled={!channelId}
					onClick={(e) => {
						e.stopPropagation();
						if (onChat && request_id) onChat(request_id);
					}}
					sx={{
						padding: '10px 20px',
						fontWeight: '700',
						fontSize: '14px',
						border: 'unset',
						...(is_paid
							? {
									backgroundColor: '#00AB89',
									'&:hover': {
										backgroundColor: '#008C6F',
									},
								}
							: {
									color: '#00AB89',
									textDecoration: 'underline',
									'&:hover': {
										backgroundColor: 'transparent',
										textDecoration: 'underline',
									},
								}),
					}}
				>
					<QuestionAnswerOutlinedIcon
						className={`mr-[10px] ${!is_paid ? 'text-[#00AB89]' : 'text-white'}`}
					/>
					<span className={!is_paid ? 'text-[#00AB89]' : 'text-white'}>{t('chat')}</span>
				</Button>

					{is_paid || offer.payment_status === 'paid' || offer.payment_status === 'stripe_payed' ? (
					<Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
						<CheckCircleIcon sx={{ color: '#3da08f', mr: 1 }} />
						<Typography sx={{ color: '#3da08f', fontWeight: 'bold' }}>{t('paid')}</Typography>
					</Box>
				) : (
					<Button
						variant="contained"
						onClick={(e) => {
							e.stopPropagation();
							if (onSendOffer) onSendOffer(id);
							if (onEditOffer) onEditOffer(id);
						}}
						sx={{
							padding: '10px 20px',
							fontWeight: '700',
							fontSize: '14px',
							border: 'unset',
							backgroundColor: is_sent ? '#FFAA2B' : '#00AB89',
							color: 'white',
						}}
							disabled={offer.payment_status === 'paid' || offer.payment_status === 'stripe_payed'}
					>
							{is_sent ? (offer.payment_status === 'paid' || offer.payment_status === 'stripe_payed' ? t('paid') : t('editOffer')) : t('sendOffer')}
					</Button>
				)}
			</Box>
			)}
		</Card>
	);
};
